import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  getDocumentCatalog,
  getDocumentsForMunicipality,
  updateDocumentCatalogPlacement,
  uploadDocumentForMunicipality,
  type DocumentCatalogEntry,
  type DocumentCatalogFolder,
  type DocumentCatalogInFlightEntry,
  type DocumentCatalogList,
  type DocumentCatalogPagination,
  type DocumentCatalogResponse,
  type DocumentIngestStatus,
} from "@/api/messages";
import CatalogRowEditor, { type CatalogPlacementDraft } from "@/components/documents/CatalogRowEditor";
import DestinationCheckboxes, {
  CONNECT_PUBLIC_NOTICE,
  submittedDestinations,
  type DestinationSelection,
} from "@/components/documents/DestinationCheckboxes";
import { useCurrentUser } from "@/context/UserProvider";
import { useToast } from "@/hooks/useToast";
import { buildChildrenByParentId } from "@/lib/documentGroupTree";
import { Upload, FileText, RefreshCw, ChevronRight } from "lucide-react";

interface Document {
  id: number;
  name: string;
  description?: string;
  status: string;
}

interface DocumentsManagerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  municipalityId: number;
  catalog?: boolean;
  canUpload?: boolean;
}

const emptyCatalog: DocumentCatalogResponse = {
  list: "research_assistant",
  counts: { research_assistant: 0, file_center: 0 },
  documents: [],
  in_flight: [],
  folders: [],
  pagination: { current_page: 1, total_pages: 0, per_page: 25, total_count: 0 },
};

const MICHAEL_NAME = "Michael Tharrington";
const MICHAEL_ROLE = "Customer Success";
const MICHAEL_EMAIL = "michael@breakground.ai";
const MICHAEL_MAILTO = `mailto:${MICHAEL_EMAIL}`;
const MICHAEL_PHOTO = "/images/michael-tharrington.jpg";
const IN_FLIGHT_PREVIEW_COUNT = 3;

const UPLOAD_AUTHORIZATION =
  "By uploading this file, you confirm you are authorized to add it to Ordinal for this city. Members of your team who can open this city's Documents, and the Ordinal team that operates the product, will be able to see it. Ordinal does not guarantee confidentiality of files stored in the product. Uploading is also covered by the Terms of Service.";

const DESTINATION_HELP =
  "File Center is available now. Research and Connect appear after processing.";

function folderOptions(folders: DocumentCatalogFolder[]) {
  const childrenByParent = buildChildrenByParentId(folders);
  const options: { id: number; label: string }[] = [];
  const walk = (parentId: number | null, depth: number) => {
    for (const folder of childrenByParent.get(parentId) ?? []) {
      options.push({ id: folder.id, label: `${"\u2014 ".repeat(depth)}${folder.name}` });
      walk(folder.id, depth + 1);
    }
  };
  walk(null, 0);
  return options;
}

const SEARCH_DELAY_MS = 300;

function formatAddedOn(iso: string) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function listLabel(list: DocumentCatalogList) {
  return list === "file_center" ? "File Center" : "Research Assistant";
}

function formatFileCount(total: number) {
  return total === 1 ? "1 file" : `${total.toLocaleString()} files`;
}

function catalogSummary(total: number, query: string, recent: boolean) {
  const files = formatFileCount(total);
  if (query && recent) return `${files} added in the last 30 days match that name.`;
  if (query) return `${files} match that name.`;
  if (recent) return `${files} added in the last 30 days.`;
  return `${files}.`;
}

function emptyListMessage(query: string, recent: boolean, label: string) {
  if (query && recent) return "No files added in the last 30 days match that name.";
  if (query) return "No files match that name.";
  if (recent) return "Nothing was added in the last 30 days.";
  return `Nothing in ${label} yet.`;
}

function getStatusBadgeClass(status: string) {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
    case 'pending':
    case 'uploaded':
    case 'processing':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
    case 'failed':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
}

function ingestLabel(status: DocumentIngestStatus) {
  switch (status) {
    case "received":
      return "Received";
    case "processing":
      return "Processing";
    case "ready":
      return "Ready";
    case "needs_attention":
      return "Needs attention";
  }
}

function ingestBadgeClass(status: DocumentIngestStatus) {
  switch (status) {
    case "received":
      return "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200";
    case "processing":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
    case "ready":
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
    case "needs_attention":
      return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
  }
}

function getStatusDisplay(status: string) {
  switch (status) {
    case 'completed':
      return 'Completed';
    case 'pending':
      return 'Pending';
    case 'uploaded':
      return 'Uploaded';
    case 'processing':
      return 'Processing';
    case 'failed':
      return 'Failed';
    default:
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
}

export function DocumentsManager({
  open,
  onOpenChange,
  municipalityId,
  catalog = false,
  canUpload = true,
}: DocumentsManagerProps) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [catalogData, setCatalogData] = useState<DocumentCatalogResponse>(emptyCatalog);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [catalogError, setCatalogError] = useState(false);
  const [list, setList] = useState<DocumentCatalogList>("research_assistant");
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [recentOnly, setRecentOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [inFlightPage, setInFlightPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadView, setUploadView] = useState(false);
  const appliedDefaultList = useRef(false);
  const { toast } = useToast();
  const currentUser = useCurrentUser();

  const [documentName, setDocumentName] = useState('');
  const [documentNotes, setDocumentNotes] = useState('');
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [destinations, setDestinations] = useState<DestinationSelection>(submittedDestinations());
  const [folderId, setFolderId] = useState('');

  const queryRef = useRef(query);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = searchInput.trim();
      if (queryRef.current === next) return;
      queryRef.current = next;
      setQuery(next);
      setPage(1);
    }, SEARCH_DELAY_MS);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    if (open) return;
    appliedDefaultList.current = false;
    queryRef.current = "";
    setSearchInput("");
    setQuery("");
    setRecentOnly(false);
    setPage(1);
    setInFlightPage(1);
    setList("research_assistant");
    setCatalogLoaded(false);
    setCatalogError(false);
    setCatalogData(emptyCatalog);
    setUploadView(false);
    setDocumentName('');
    setDocumentNotes('');
    setDocumentFile(null);
    setDestinations(submittedDestinations());
    setFolderId('');
  }, [open]);

  useEffect(() => {
    if (!open || !municipalityId) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      if (catalog) setCatalogError(false);
      try {
        if (catalog) {
          const data = await getDocumentCatalog(municipalityId, {
            list,
            q: query,
            recent: recentOnly,
            page,
            inFlightPage,
          });
          if (cancelled) return;

          const fileCenterOnly =
            !appliedDefaultList.current &&
            data.counts.research_assistant === 0 &&
            data.counts.file_center > 0 &&
            list === "research_assistant" &&
            query === "" &&
            !recentOnly;
          appliedDefaultList.current = true;
          if (fileCenterOnly) {
            setList("file_center");
            return;
          }

          const returnedInFlightPage = data.in_flight_pagination?.current_page;
          if (returnedInFlightPage != null) {
            setInFlightPage(returnedInFlightPage);
          }
          setCatalogData(data);
          setCatalogLoaded(true);
        } else {
          const result = await getDocumentsForMunicipality(municipalityId);
          if (cancelled) return;
          const docs: Document[] = Array.isArray(result) ? result : (result?.documents || []);
          setDocuments(docs);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch documents:', error);
        if (catalog) setCatalogError(true);
        toast({
          title: 'Error',
          description: 'Failed to load documents',
          variant: 'destructive',
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [open, municipalityId, catalog, list, query, recentOnly, page, inFlightPage, reloadKey]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setDocumentFile(null);
      return;
    }
    if (catalog && !file.name.toLowerCase().endsWith(".pdf")) {
      e.target.value = "";
      setDocumentFile(null);
      toast({
        title: "PDF only",
        description: "Choose a PDF. If the file is not a PDF, contact Customer Success.",
        variant: "destructive",
      });
      return;
    }
    setDocumentFile(file);
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!municipalityId) {
      console.error("Missing municipality ID");
      toast({
        title: 'Error',
        description: 'Missing municipality ID. Please select a municipality first.',
        variant: 'destructive',
      });
      return;
    }

    if (!documentFile || (catalog && !documentFile.name.toLowerCase().endsWith(".pdf"))) {
      console.warn("Upload attempted without a PDF");
      toast({
        title: 'PDF only',
        description: 'Choose a PDF. If the file is not a PDF, contact Customer Success.',
        variant: 'destructive',
      });
      return;
    }

    if (!documentName) {
      toast({
        title: 'Error',
        description: 'Please provide a name for the document',
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);
    try {
      const currentMunicipalityId = municipalityId;

      await uploadDocumentForMunicipality(currentMunicipalityId, {
        name: documentName,
        notes: documentNotes,
        file: documentFile,
        file_center: destinations.file_center,
        connect: destinations.connect,
        research_assistant: destinations.research_assistant,
        municipality_document_group_id: folderId || undefined,
      });

      toast({
        title: 'Success',
        description: 'Document uploaded successfully. The Ordinal team will review it before it is processed.',
      });

      setDocumentName('');
      setDocumentNotes('');
      setDocumentFile(null);
      setDestinations(submittedDestinations());
      setFolderId('');
      setUploadView(false);
      setInFlightPage(1);
      setReloadKey((key) => key + 1);
    } catch (error: any) {
      console.error('Upload failed:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to upload document',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const savePlacement = async (documentId: number, placement: CatalogPlacementDraft) => {
    try {
      await updateDocumentCatalogPlacement(municipalityId, documentId, {
        file_center: placement.file_center,
        connect: placement.connect,
        research_assistant: placement.research_assistant,
        municipality_document_group_id: placement.municipality_document_group_id
          ? Number(placement.municipality_document_group_id)
          : null,
      });
      toast({
        title: 'Saved',
        description: 'Where this file appears was updated.',
      });
      setReloadKey((key) => key + 1);
    } catch (error: unknown) {
      const description = error instanceof Error ? error.message : 'Where this file appears could not be saved.';
      toast({
        title: 'Error',
        description,
        variant: 'destructive',
      });
      throw error;
    }
  };

  const allowUpload = catalog && canUpload;
  const showUploadForm = uploadView && allowUpload;
  const catalogsEmpty = catalogLoaded
    && catalogData.counts.research_assistant === 0
    && catalogData.counts.file_center === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={catalog ? "max-w-3xl overflow-x-hidden [&>*]:min-w-0" : "sm:max-w-md"}>
        <DialogHeader>
          <DialogTitle>
            {showUploadForm ? 'Upload New Document' : catalog ? 'Documents' : 'Manage Documents'}
          </DialogTitle>
          {!showUploadForm && (
            <DialogDescription>
              {catalog
                ? 'Search by file name to see what is already in Research Assistant or File Center.'
                : 'View documents for this city.'}
            </DialogDescription>
          )}
        </DialogHeader>

        {showUploadForm ? (
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="document-name">Document Name</Label>
              <Input
                id="document-name"
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
                placeholder="Enter document name"
                required
              />
            </div>

            <div className="space-y-1">
              <p className="text-sm font-medium">Created by</p>
              <p className="text-sm text-muted-foreground">{currentUser?.email}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="document-description">Description (optional)</Label>
              <Textarea
                id="document-description"
                value={documentNotes}
                onChange={(e) => setDocumentNotes(e.target.value)}
                placeholder="Describe this document"
                rows={3}
              />
            </div>

            <DestinationCheckboxes
              documentType="hierarchical"
              value={destinations}
              onChange={setDestinations}
              help={DESTINATION_HELP}
              connectNotice={CONNECT_PUBLIC_NOTICE}
              connectRequiresResearch
              researchBeforeConnect
            />

            <div className="space-y-2">
              <Label htmlFor="document-folder">Folder (optional)</Label>
              <select
                id="document-folder"
                value={folderId}
                onChange={(event) => setFolderId(event.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
              >
                <option value="">No folder</option>
                {folderOptions(catalogData.folders ?? []).map((folder) => (
                  <option key={folder.id} value={String(folder.id)}>
                    {folder.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="document-file">File</Label>
              <Input
                id="document-file"
                type="file"
                onChange={handleFileChange}
                accept=".pdf"
              />
            </div>

            <p className="text-sm text-muted-foreground">{UPLOAD_AUTHORIZATION}</p>

            <div className="flex justify-between pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setUploadView(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={uploading || !documentName || !documentFile}
              >
                {uploading ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload
                  </>
                )}
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              If the file is not a PDF, contact Customer Success.
            </p>
          </form>
        ) : (
          <>
            <div className="flex justify-between items-center">
              <Button
                type="button"
                onClick={() => setReloadKey((key) => key + 1)}
                variant="outline"
                size="sm"
                disabled={loading}
              >
                {loading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                <span className="sr-only">Refresh</span>
              </Button>
              {allowUpload && (
                <Button
                  type="button"
                  onClick={() => setUploadView(true)}
                  size="sm"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Upload New
                </Button>
              )}
            </div>

            {catalog ? (
              catalogError ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  The catalog could not be loaded.
                </p>
              ) : !catalogLoaded ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin h-8 w-8 border-2 border-primary rounded-full border-t-transparent"></div>
                </div>
              ) : (
                <CatalogBrowser
                  catalogData={catalogData}
                  loading={loading}
                  list={list}
                  page={page}
                  query={query}
                  recentOnly={recentOnly}
                  catalogsEmpty={catalogsEmpty}
                  canUpload={canUpload}
                  onListChange={(next) => {
                    setList(next);
                    setPage(1);
                  }}
                  onSearchInput={setSearchInput}
                  searchInput={searchInput}
                  onRecentOnly={(next) => {
                    setRecentOnly(next);
                    setPage(1);
                  }}
                  onPrevious={() => setPage((current) => Math.max(1, current - 1))}
                  onNext={() => setPage((current) => current + 1)}
                  onInFlightPrevious={() =>
                    setInFlightPage(Math.max(1, (catalogData.in_flight_pagination?.current_page ?? inFlightPage) - 1))
                  }
                  onInFlightNext={() =>
                    setInFlightPage((catalogData.in_flight_pagination?.current_page ?? inFlightPage) + 1)
                  }
                  onInFlightShowFewer={() => setInFlightPage(1)}
                  onUpload={() => setUploadView(true)}
                  onSavePlacement={savePlacement}
                />
              )
            ) : (
              <LegacyDocumentList
                documents={documents}
                loading={loading}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LegacyDocumentList({
  documents,
  loading,
}: {
  documents: Document[];
  loading: boolean;
}) {
  return (
    <div className="max-h-[60vh] overflow-auto border rounded-lg p-4">
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin h-8 w-8 border-2 border-primary rounded-full border-t-transparent"></div>
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <FileText className="mx-auto h-12 w-12 opacity-50 mb-2" />
          <p>No documents found</p>
        </div>
      ) : (
        <div className="space-y-6">
          {documents.some(doc => doc.status !== 'completed') && (
            <div className="mb-4">
              <h3 className="text-sm font-semibold mb-2">Processing Documents</h3>
              <div className="space-y-3">
                {documents.filter(doc => doc.status !== 'completed').map((doc) => (
                  <div key={doc.id} className="p-3 border rounded-md opacity-80">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium">{doc.name}</h4>
                        {doc.description && (
                          <p className="text-sm text-muted-foreground mt-1">{doc.description}</p>
                        )}
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadgeClass(doc.status)}`}>
                        {getStatusDisplay(doc.status)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <h3 className="text-sm font-semibold mb-2">Available Documents</h3>
            <div className="space-y-3">
              {documents.filter(doc => doc.status === 'completed').map((doc) => (
                <div key={doc.id} className="p-3 border rounded-md">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium">{doc.name}</h4>
                      {doc.description && (
                        <p className="text-sm text-muted-foreground mt-1">{doc.description}</p>
                      )}
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadgeClass(doc.status)}`}>
                      {getStatusDisplay(doc.status)}
                    </span>
                  </div>
                </div>
              ))}
              {documents.filter(doc => doc.status === 'completed').length === 0 && (
                <p className="text-center text-muted-foreground py-2">No available documents</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CatalogBrowser({
  catalogData,
  loading,
  list,
  page,
  query,
  searchInput,
  recentOnly,
  catalogsEmpty,
  canUpload,
  onListChange,
  onSearchInput,
  onRecentOnly,
  onPrevious,
  onNext,
  onInFlightPrevious,
  onInFlightNext,
  onInFlightShowFewer,
  onUpload,
  onSavePlacement,
}: {
  catalogData: DocumentCatalogResponse;
  loading: boolean;
  list: DocumentCatalogList;
  page: number;
  query: string;
  searchInput: string;
  recentOnly: boolean;
  catalogsEmpty: boolean;
  canUpload: boolean;
  onListChange: (list: DocumentCatalogList) => void;
  onSearchInput: (value: string) => void;
  onRecentOnly: (recent: boolean) => void;
  onPrevious: () => void;
  onNext: () => void;
  onInFlightPrevious: () => void;
  onInFlightNext: () => void;
  onInFlightShowFewer: () => void;
  onUpload: () => void;
  onSavePlacement: (documentId: number, placement: CatalogPlacementDraft) => Promise<void>;
}) {
  const inFlight = catalogData.in_flight ?? [];

  return (
    <div className="min-w-0 space-y-4">
      {inFlight.length > 0 && (
        <InFlightUploads
          rows={inFlight}
          pagination={catalogData.in_flight_pagination}
          loading={loading}
          onPrevious={onInFlightPrevious}
          onNext={onInFlightNext}
          onShowFewer={onInFlightShowFewer}
        />
      )}
      {catalogsEmpty ? (
        <CatalogEmpty canUpload={canUpload} onUpload={onUpload} />
      ) : (
        <CatalogLibrary
          catalogData={catalogData}
          loading={loading}
          list={list}
          page={page}
          query={query}
          searchInput={searchInput}
          recentOnly={recentOnly}
          canUpload={canUpload}
          onListChange={onListChange}
          onSearchInput={onSearchInput}
          onRecentOnly={onRecentOnly}
          onPrevious={onPrevious}
          onNext={onNext}
          onSavePlacement={onSavePlacement}
        />
      )}
    </div>
  );
}

function CatalogEmpty({
  canUpload,
  onUpload,
}: {
  canUpload: boolean;
  onUpload: () => void;
}) {
  return (
    <div className="text-center py-8 text-muted-foreground">
      <FileText className="mx-auto h-12 w-12 opacity-50 mb-2" />
      <p>No documents in Research Assistant or File Center yet.</p>
      <p className="mt-2">
        {canUpload
          ? "You can upload one file. If you have many files, or you do not have a file, open Help to contact support."
          : "If you have many files, or you do not have a file, open Help to contact support."}
      </p>
      {canUpload && (
        <Button
          type="button"
          onClick={onUpload}
          variant="outline"
          className="mt-4"
        >
          Upload one file
        </Button>
      )}
    </div>
  );
}

function CatalogLibrary({
  catalogData,
  loading,
  list,
  page,
  query,
  searchInput,
  recentOnly,
  canUpload,
  onListChange,
  onSearchInput,
  onRecentOnly,
  onPrevious,
  onNext,
  onSavePlacement,
}: {
  catalogData: DocumentCatalogResponse;
  loading: boolean;
  list: DocumentCatalogList;
  page: number;
  query: string;
  searchInput: string;
  recentOnly: boolean;
  canUpload: boolean;
  onListChange: (list: DocumentCatalogList) => void;
  onSearchInput: (value: string) => void;
  onRecentOnly: (recent: boolean) => void;
  onPrevious: () => void;
  onNext: () => void;
  onSavePlacement: (documentId: number, placement: CatalogPlacementDraft) => Promise<void>;
}) {
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const label = listLabel(list);
  const { documents, pagination } = catalogData;
  const folders = folderOptions(catalogData.folders ?? []);

  useEffect(() => {
    setExpandedId(null);
  }, [list, page]);

  return (
    <div className="min-w-0 space-y-4">
      <Tabs
        value={list}
        onValueChange={(value) => onListChange(value as DocumentCatalogList)}
      >
        <TabsList className="grid h-auto w-full min-w-0 grid-cols-2">
          <TabsTrigger value="research_assistant" className="h-auto min-w-0 whitespace-normal px-3 py-2">
            <span className="flex flex-col items-center leading-tight">
              <span>Research Assistant</span>
              <span className="text-xs font-normal">{catalogData.counts.research_assistant.toLocaleString()}</span>
            </span>
          </TabsTrigger>
          <TabsTrigger value="file_center" className="h-auto min-w-0 whitespace-normal px-3 py-2">
            <span className="flex flex-col items-center leading-tight">
              <span>File Center</span>
              <span className="text-xs font-normal">{catalogData.counts.file_center.toLocaleString()}</span>
            </span>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="min-w-0 flex-1 space-y-1">
          <Label htmlFor="catalog-search">Search by name</Label>
          <Input
            id="catalog-search"
            value={searchInput}
            onChange={(event) => onSearchInput(event.target.value)}
            placeholder="Zoning code"
            autoComplete="off"
          />
        </div>
        <div className="flex min-w-0 items-center gap-2 pb-2">
          <Checkbox
            id="catalog-recent"
            checked={recentOnly}
            onCheckedChange={(checked) => onRecentOnly(checked === true)}
          />
          <Label htmlFor="catalog-recent" className="min-w-0 font-normal">
            Added in the last 30 days
          </Label>
        </div>
      </div>

      <div aria-busy={loading}>
        <p className="mb-2 min-w-0 text-sm text-muted-foreground [overflow-wrap:anywhere]">
          {loading ? "Loading files…" : catalogSummary(pagination.total_count, query, recentOnly)}
          {documents.length > 0 && (canUpload
            ? " Select a file to read its description and choose where it appears."
            : " Select a file to read its description.")}
        </p>
        {documents.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            {emptyListMessage(query, recentOnly, label)}
          </p>
        ) : (
          <ul className="min-w-0 divide-y rounded-md border">
            {documents.map((doc) => (
              <CatalogDocumentRow
                key={doc.id}
                doc={doc}
                expanded={expandedId === doc.id}
                canManage={canUpload}
                folders={folders}
                onToggle={() => setExpandedId((current) => current === doc.id ? null : doc.id)}
                onSavePlacement={onSavePlacement}
              />
            ))}
          </ul>
        )}
      </div>

      {pagination.total_pages > 1 && (
        <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onPrevious}
            disabled={loading || pagination.current_page <= 1}
          >
            Previous
          </Button>
          <p className="text-sm text-muted-foreground">
            Page {pagination.current_page} of {pagination.total_pages}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onNext}
            disabled={loading || pagination.current_page >= pagination.total_pages}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

function InFlightUploads({
  rows,
  pagination,
  loading,
  onPrevious,
  onNext,
  onShowFewer,
}: {
  rows: DocumentCatalogInFlightEntry[];
  pagination?: DocumentCatalogPagination;
  loading: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onShowFewer: () => void;
}) {
  const [showAll, setShowAll] = useState(false);
  const totalCount = pagination?.total_count ?? rows.length;
  const totalPages = pagination?.total_pages ?? 0;
  const hasMore = totalCount > IN_FLIGHT_PREVIEW_COUNT || totalPages > 1;
  const visibleRows = showAll || !hasMore ? rows : rows.slice(0, IN_FLIGHT_PREVIEW_COUNT);

  return (
    <section aria-labelledby="uploads-in-progress" className="min-w-0 space-y-2">
      <div className="flex min-w-0 flex-wrap items-baseline justify-between gap-2">
        <h3 id="uploads-in-progress" className="text-sm font-semibold">Uploads in progress</h3>
        <p className="text-sm text-muted-foreground">{formatFileCount(totalCount)}</p>
      </div>
      <ul className="min-w-0 divide-y overflow-hidden rounded-md border">
        {visibleRows.map((doc) => (
          <InFlightUploadRow key={doc.id} doc={doc} />
        ))}
      </ul>
      {hasMore && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            if (showAll) {
              setShowAll(false);
              onShowFewer();
              return;
            }
            setShowAll(true);
          }}
        >
          {showAll ? "Show fewer uploads" : "View all uploads"}
        </Button>
      )}
      {showAll && totalPages > 1 && pagination && (
        <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onPrevious}
            disabled={loading || pagination.current_page <= 1}
            aria-label="Previous uploads"
          >
            Previous
          </Button>
          <p className="text-sm text-muted-foreground">
            Page {pagination.current_page} of {pagination.total_pages}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onNext}
            disabled={loading || pagination.current_page >= pagination.total_pages}
            aria-label="Next uploads"
          >
            Next
          </Button>
        </div>
      )}
    </section>
  );
}

function InFlightUploadRow({ doc }: { doc: DocumentCatalogInFlightEntry }) {
  const [contactOpen, setContactOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const contactId = `michael-contact-${doc.id}`;
  const helpId = `needs-attention-${doc.id}`;
  const showContact = contactOpen && doc.michael_pending;
  const showHelp = helpOpen && doc.ingest_status === "needs_attention";

  useEffect(() => {
    if (!doc.michael_pending) setContactOpen(false);
  }, [doc.michael_pending]);

  useEffect(() => {
    if (doc.ingest_status !== "needs_attention") setHelpOpen(false);
  }, [doc.ingest_status]);

  return (
    <li className="min-w-0 px-3 py-2">
      <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
        <p className="min-w-0 flex-1 font-medium [overflow-wrap:anywhere]">{doc.name}</p>
        <div className="flex flex-wrap items-center gap-2">
          {doc.michael_pending && (
            <button
              type="button"
              className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-expanded={showContact}
              aria-controls={contactId}
              onClick={() => setContactOpen((current) => !current)}
            >
              <img
                src={MICHAEL_PHOTO}
                alt=""
                className="h-8 w-8 rounded-full object-cover"
              />
              <span className="sr-only">Contact {MICHAEL_NAME}, {MICHAEL_ROLE}</span>
            </button>
          )}
          {doc.michael_pending && (
            <a
              href={MICHAEL_MAILTO}
              className="inline-flex items-center whitespace-nowrap rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-800 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:bg-slate-800 dark:text-slate-200"
            >
              {MICHAEL_EMAIL}
            </a>
          )}
          <span
            aria-live="polite"
            className={`text-xs px-2 py-1 rounded-full ${ingestBadgeClass(doc.ingest_status)}`}
          >
            {ingestLabel(doc.ingest_status)}
          </span>
          {doc.ingest_status === "needs_attention" && (
            <button
              type="button"
              className="text-sm text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-expanded={showHelp}
              aria-controls={helpId}
              onClick={() => setHelpOpen((current) => !current)}
            >
              What to do next
            </button>
          )}
        </div>
      </div>
      {showContact && (
        <div id={contactId} className="mt-2 max-w-sm rounded-md border bg-muted/40 p-3 text-sm">
          <p className="font-medium">{MICHAEL_NAME}</p>
          <p className="text-muted-foreground">{MICHAEL_ROLE}</p>
          <p className="mt-2">{MICHAEL_NAME} is reviewing this file.</p>
        </div>
      )}
      {showHelp && (
        <div id={helpId} className="mt-2 max-w-sm text-sm text-muted-foreground">
          <p>
            {MICHAEL_NAME} from {MICHAEL_ROLE} owns the next step. Wait for his review. You do not need to upload this file again.
          </p>
          <a href={MICHAEL_MAILTO} className="mt-2 inline-block text-primary underline underline-offset-4">
            Email Michael
          </a>
        </div>
      )}
    </li>
  );
}

function CatalogDocumentRow({
  doc,
  expanded,
  canManage,
  folders,
  onToggle,
  onSavePlacement,
}: {
  doc: DocumentCatalogEntry;
  expanded: boolean;
  canManage: boolean;
  folders: { id: number; label: string }[];
  onToggle: () => void;
  onSavePlacement: (documentId: number, placement: CatalogPlacementDraft) => Promise<void>;
}) {
  const addedOn = formatAddedOn(doc.added_at);
  const description = doc.description?.trim();

  return (
    <li className="min-w-0">
      <button
        type="button"
        className="flex w-full min-w-0 items-start gap-2 px-3 py-2 text-left hover:bg-muted/60"
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <ChevronRight
          aria-hidden
          className={`mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform ${expanded ? "rotate-90" : ""}`}
        />
        <span className="min-w-0 flex-1">
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate font-medium" title={doc.name}>{doc.name}</span>
            {doc.connect && (
              <span
                className="shrink-0 text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                title="Also on Connect"
              >
                Connect
              </span>
            )}
          </span>
          {addedOn && (
            <span className="mt-0.5 block text-xs text-muted-foreground">Added {addedOn}</span>
          )}
        </span>
      </button>
      {expanded && (
        <div className="min-w-0 space-y-3 px-3 pb-3">
          <p className="text-sm text-muted-foreground [overflow-wrap:anywhere]">
            {description || "No description on file."}
          </p>
          {canManage && (
            <CatalogRowEditor
              fileCenter={doc.file_center === true}
              researchAssistant={doc.research_assistant === true}
              connect={doc.connect_selected ?? doc.connect === true}
              folderId={doc.municipality_document_group_id ?? null}
              folders={folders}
              onSave={(placement) => onSavePlacement(doc.id, placement)}
            />
          )}
        </div>
      )}
    </li>
  );
}

export default DocumentsManager;
